import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Scale, Target, TrendingDown, Award, ArrowDown,
  ArrowUp, Activity, Hash, Flame, Trophy, Calculator, Percent, Pencil, Layers
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { useWeightEntries } from '../../hooks/useWeightEntries'
import { useBodyMeasurements } from '../../hooks/useBodyMeasurements'
import { useSessions } from '../../hooks/useSessions'
import { useMealEntries } from '../../hooks/useMealEntries'
import { useProfile } from '../../hooks/useProfile'
import { useCurrentPhase } from '../../hooks/useCurrentPhase'
import {
  computeProgressStats, computeBMI, getBMICategory, formatMeasurement,
  computeMilestones, computePace, computePhaseStats,
} from '../../lib/phoenixUtils'
import {
  resolveRange, weightCaloriesSeries, weeklyWeightChange,
  macroAdherence, trainingRestStats, loggingStreaks, generateInsights,
} from '../../lib/analysisUtils'
import { useUnits } from '../../contexts/UnitsContext'
import { StatCard } from './components/StatCard'
import { ProgressBar } from './components/ProgressBar'
import { HeatMapCalendar } from './components/HeatMapCalendar'
import { MilestonesCard } from './components/MilestonesCard'
import { CurrentPhaseCard } from './components/CurrentPhaseCard'
import { AnalysisRangePicker } from './components/analysis/AnalysisRangePicker'
import { InsightsFeed } from './components/analysis/InsightsFeed'
import { WeightCaloriesChart } from './components/analysis/WeightCaloriesChart'
import { MacroAdherence } from './components/analysis/MacroAdherence'
import { TrainingRestMatrix } from './components/analysis/TrainingRestMatrix'
import { LoggingStreaks } from './components/analysis/LoggingStreaks'
import { cn } from '../../lib/utils'

function openPhaseManager(navigate) {
  navigate('/phases')
}

function openEditWizard() {
  window.dispatchEvent(new Event('phoenix:edit-profile'))
}

const BMI_CATEGORY_COLORS = {
  underweight: 'text-blue-500',
  normal: 'text-green-500',
  overweight: 'text-amber-500',
  obese: 'text-red-500',
}

const PACE_COLORS = {
  ahead: 'text-green-500',
  on: 'text-app-primary-500',
  behind: 'text-red-500',
}

export function ProgressTab() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { weightUnit, formatWeight } = useUnits()
  const { entries, loading, error, refetch } = useWeightEntries()
  const { measurements } = useBodyMeasurements()
  const { sessions } = useSessions()
  const { entries: mealEntries } = useMealEntries()
  const { profile } = useProfile()
  const { currentPhase } = useCurrentPhase()

  const startWeight = profile?.start_weight
  const goalWeight = profile?.goal_weight
  const goalDate = profile?.goal_date

  if (loading && entries.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-full" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (error && entries.length === 0) {
    return (
      <Card className="border-red-500/50">
        <CardContent className="text-center py-8">
          <p className="text-red-500 mb-4">{t('phoenixTracker.error')}</p>
          <Button onClick={refetch} variant="outline">{t('phoenixTracker.retry')}</Button>
        </CardContent>
      </Card>
    )
  }

  if (startWeight == null || goalWeight == null) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Target className="w-12 h-12 mx-auto mb-4 text-app-primary-500/50" />
          <p className="mb-4">{t('phoenixTracker.progress.incompleteGoals')}</p>
          <Button
            onClick={openEditWizard}
            className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
          >
            {t('phoenixTracker.progress.completeGoalsCta')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Scale className="w-12 h-12 mx-auto mb-4 text-app-primary-500/50" />
          <p>{t('phoenixTracker.progress.noData')}</p>
        </CardContent>
      </Card>
    )
  }

  const stats = computeProgressStats(entries, startWeight, goalWeight)
  const milestones = computeMilestones(entries, startWeight, goalWeight)

  // Phase pace takes priority when there's a current phase with goal + end_date.
  // Falls back to the profile-level pace otherwise.
  const phaseStats = currentPhase ? computePhaseStats(currentPhase, entries) : null
  const usePhasePace = !!(
    currentPhase
    && currentPhase.goal_weight_kg != null
    && currentPhase.end_date
    && phaseStats
  )
  const pace = usePhasePace
    ? computePace({
        currentWeight: phaseStats.currentWeight ?? stats.currentWeight,
        goalWeight: Number(currentPhase.goal_weight_kg),
        goalDate: currentPhase.end_date,
        recentRatePerWeek: phaseStats.recentRatePerWeek,
      })
    : computePace({
        currentWeight: stats.currentWeight,
        goalWeight,
        goalDate,
        recentRatePerWeek: stats.avgLossRate7Day,
      })
  const paceKeyBase = usePhasePace ? 'phoenixTracker.phases.pacePhase' : 'phoenixTracker.progress.pace'

  // BMI calculation
  const bmi = computeBMI(stats.currentWeight, profile?.height_cm)
  const bmiCategory = getBMICategory(bmi)
  const bmiColor = bmiCategory ? BMI_CATEGORY_COLORS[bmiCategory] : 'text-app-primary-500'

  // Latest body fat from measurements
  const latestBodyFat = measurements.find((m) => m.body_fat != null)?.body_fat ?? null

  // Analysis range — persisted in localStorage so a manual selection
  // survives reloads. Default is 7d (the shortest window) when no
  // saved preference is present.
  const [analysisRange, setAnalysisRange] = useState(() => {
    if (typeof window === 'undefined') return '7d'
    const saved = window.localStorage.getItem('routine-app-analysis-range')
    return saved || '7d'
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('routine-app-analysis-range', analysisRange)
  }, [analysisRange])
  const resolvedRange = useMemo(
    () => resolveRange(analysisRange, currentPhase),
    [analysisRange, currentPhase]
  )

  const wcSeries = useMemo(
    () => weightCaloriesSeries({ weightEntries: entries, mealEntries, sessions, profile, range: resolvedRange }),
    [entries, mealEntries, sessions, profile, resolvedRange]
  )
  const adherence = useMemo(
    () => macroAdherence({ mealEntries, profile, range: resolvedRange }),
    [mealEntries, profile, resolvedRange]
  )
  const trainingVsRest = useMemo(
    () => trainingRestStats({
      mealEntries, sessions, weightEntries: entries, profile, range: resolvedRange,
    }),
    [mealEntries, sessions, entries, profile, resolvedRange]
  )
  const streaks = useMemo(
    () => loggingStreaks({ weightEntries: entries, mealEntries }),
    [entries, mealEntries]
  )
  const weeklyChange = useMemo(
    () => weeklyWeightChange({ weightEntries: entries, range: resolvedRange }),
    [entries, resolvedRange]
  )
  const insights = useMemo(
    () => generateInsights({
      weightEntries: entries,
      mealEntries,
      sessions,
      profile,
      range: resolvedRange,
      weeklyChange,
      adherence,
      trainingVsRest,
    }),
    [entries, mealEntries, sessions, profile, resolvedRange, weeklyChange, adherence, trainingVsRest]
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          {pace && (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 text-xs font-medium',
                PACE_COLORS[pace.status]
              )}
            >
              <Activity className="w-3.5 h-3.5" />
              {pace.status === 'on'
                ? t(`${paceKeyBase}.on`)
                : t(`${paceKeyBase}.${pace.status}`, {
                    delta: formatWeight(Math.abs(pace.delta)),
                    unit: weightUnit,
                  })}
            </span>
          )}
        </div>
        <Button
          onClick={openEditWizard}
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
        >
          <Pencil className="w-3.5 h-3.5 me-1.5" />
          {t('phoenixTracker.onboarding.nav.edit')}
        </Button>
      </div>

      <ProgressBar
        startWeight={startWeight}
        goalWeight={goalWeight}
        currentWeight={stats.currentWeight}
      />

      <CurrentPhaseCard phase={currentPhase} weightEntries={entries} />

      {!currentPhase && (
        <button
          type="button"
          onClick={() => openPhaseManager(navigate)}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-border bg-card/40 hover:bg-card hover:border-app-primary-500/40 transition-colors text-start"
        >
          <Layers className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
          <span className="text-sm text-foreground flex-1">
            {t('phoenixTracker.phases.title')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('phoenixTracker.phases.noCurrent')}
          </span>
        </button>
      )}

      {/* Analysis sections — global range picker + cross-domain views. */}
      <div className="pt-1">
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold" dir="auto">
            {t('phoenixTracker.analysis.sectionTitle')}
          </h2>
          <AnalysisRangePicker
            value={analysisRange}
            onChange={setAnalysisRange}
            hasPhase={!!currentPhase?.start_date}
          />
        </div>

        <div className="space-y-3">
          <InsightsFeed insights={insights} />
          <WeightCaloriesChart series={wcSeries} />
          <MacroAdherence adherence={adherence} />
          <TrainingRestMatrix stats={trainingVsRest} />
          <LoggingStreaks streaks={streaks} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
        <StatCard
          label={t('phoenixTracker.progress.startWeight')}
          value={formatWeight(startWeight)}
          unit={weightUnit}
          icon={Scale}
          colorClass="text-gray-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.goalWeight')}
          value={formatWeight(goalWeight)}
          unit={weightUnit}
          icon={Target}
          colorClass="text-blue-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.currentWeight')}
          value={formatWeight(stats.currentWeight)}
          unit={weightUnit}
          icon={Activity}
          colorClass="text-app-primary-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.totalLost')}
          value={formatWeight(stats.totalLost)}
          unit={weightUnit}
          icon={TrendingDown}
          colorClass={stats.totalLost > 0 ? 'text-green-500' : 'text-red-500'}
        />
        <StatCard
          label={t('phoenixTracker.progress.remaining')}
          value={formatWeight(stats.remaining)}
          unit={weightUnit}
          icon={ArrowDown}
          colorClass="text-amber-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.avgLossRate')}
          value={formatWeight(stats.avgLossRate7Day)}
          unit={`${weightUnit}/${t('phoenixTracker.progress.weekShort')}`}
          icon={Activity}
          colorClass="text-purple-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.lowestWeight')}
          value={formatWeight(stats.lowestWeight)}
          unit={weightUnit}
          icon={Award}
          colorClass="text-green-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.highestWeight')}
          value={formatWeight(stats.highestWeight)}
          unit={weightUnit}
          icon={ArrowUp}
          colorClass="text-red-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.totalEntries')}
          value={stats.totalEntries}
          icon={Hash}
          colorClass="text-app-primary-500"
        />
        <StatCard
          label={t('phoenixTracker.progress.currentStreak')}
          value={stats.currentStreak}
          unit={t('phoenixTracker.progress.days')}
          icon={Flame}
          colorClass={stats.currentStreak > 0 ? 'text-app-primary-500' : 'text-muted-foreground'}
        />
        <StatCard
          label={t('phoenixTracker.progress.longestStreak')}
          value={stats.longestStreak}
          unit={t('phoenixTracker.progress.days')}
          icon={Trophy}
          colorClass="text-amber-500"
        />
        {bmi != null && (
          <StatCard
            label={`${t('phoenixTracker.progress.bmi')} · ${t(`phoenixTracker.progress.category.${bmiCategory}`)}`}
            value={bmi.toFixed(1)}
            icon={Calculator}
            colorClass={bmiColor}
          />
        )}
        {latestBodyFat != null && (
          <StatCard
            label={t('phoenixTracker.progress.bodyFat')}
            value={formatMeasurement(latestBodyFat)}
            unit="%"
            icon={Percent}
            colorClass="text-purple-500"
          />
        )}
      </div>

      <MilestonesCard milestones={milestones} currentWeight={stats.currentWeight} />

      <HeatMapCalendar entries={entries} />
    </div>
  )
}
